import React, { useEffect, useState, useRef } from 'react';
import { buscarMunicipios } from '../services/municipiosIbge';

// ============================================================
// ESTILOS BASE
// ============================================================
export const cores = {
  verde: '#2563EB',
  verdeClaro: '#EFF6FF',
  verdeTxt: '#1E40AF',
  vermelho: '#E24B4A',
  vermelhoClaro: '#FCEBEB',
  vermelhoTxt: '#791F1F',
  azul: '#185FA5',
  azulClaro: '#E6F1FB',
  azulTxt: '#0C447C',
  roxo: '#534AB7',
  roxoClaro: '#EEEDFE',
  roxoTxt: '#3C3489',
  amarelo: '#BA7517',
  amareloClaro: '#FAEEDA',
  amareloTxt: '#633806',
  cinzaBg: '#f8f8f6',
  cinzaBorda: '#e8e8e6',
  cinzaTxt: '#444441',
  cinzaLight: '#888',
  branco: '#ffffff',
};

// ============================================================
// COMPONENTES
// ============================================================
export const StatusBadge = ({ status }) => {
  const map = {
    autorizada: { bg: cores.verdeClaro, color: cores.verdeTxt },
    cancelada: { bg: cores.vermelhoClaro, color: cores.vermelhoTxt },
    rejeitada: { bg: cores.vermelhoClaro, color: cores.vermelhoTxt },
    rascunho: { bg: '#F1EFE8', color: '#444441' },
    assinada: { bg: cores.azulClaro, color: cores.azulTxt },
    transmitida: { bg: cores.amareloClaro, color: cores.amareloTxt },
    inutilizada: { bg: '#E5E7EB', color: '#374151' },
    reemitida: { bg: '#E5E7EB', color: '#374151' },
  };
  const s = map[status] || map.rascunho;
  return (
    <span style={{
      background: s.bg, color: s.color, padding: '3px 12px',
      borderRadius: '6px', fontSize: '12px', fontWeight: 500,
    }}>
      {(status || '').charAt(0).toUpperCase() + (status || '').slice(1)}
    </span>
  );
};

export const Card = ({ titulo, valor, sub, cor }) => (
  <div style={{ background: cores.cinzaBg, borderRadius: '10px', padding: '1rem 1.25rem' }}>
    <p style={{ fontSize: '12px', color: cores.cinzaLight, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{titulo}</p>
    <p style={{ fontSize: '26px', fontWeight: 600, margin: 0, color: cor || '#1a1a1a' }}>{valor}</p>
    {sub && <p style={{ fontSize: '12px', color: '#aaa', margin: '4px 0 0' }}>{sub}</p>}
  </div>
);

// Contador "n/máx" para campos com limite do layout da NF-e. Conta caracteres
// (acentos = 1) após trim, como vai no XML. Fica vermelho quando um valor
// antigo, gravado antes do limite, ainda passa do máximo.
export const ContadorCaracteres = ({ valor, max }) => {
  const n = String(valor ?? '').trim().length;
  const excedeu = n > max;
  return (
    <div style={{ fontSize: '11px', textAlign: 'right', marginTop: '2px',
                  color: excedeu ? '#B91C1C' : '#999', fontWeight: excedeu ? 600 : 400 }}>
      {n}/{max}{excedeu ? ' — acima do limite da NF-e, abrevie' : ''}
    </div>
  );
};

export const Input = ({ label, style: extraStyle, contador, ...props }) => (
  <div style={{ marginBottom: '14px', ...extraStyle }}>
    {label && <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>{label}</label>}
    <input style={{
      width: '100%', padding: '9px 12px', border: `1px solid ${cores.cinzaBorda}`,
      borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
      outline: 'none', transition: 'border-color 0.2s',
    }} {...props} />
    {contador && props.maxLength && <ContadorCaracteres valor={props.value} max={props.maxLength} />}
  </div>
);

export const Select = ({ label, children, ...props }) => (
  <div style={{ marginBottom: '14px' }}>
    {label && <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>{label}</label>}
    <select style={{
      width: '100%', padding: '9px 12px', border: `1px solid ${cores.cinzaBorda}`,
      borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
      background: cores.branco, outline: 'none',
    }} {...props}>{children}</select>
  </div>
);

export const Textarea = ({ label, ...props }) => (
  <div style={{ marginBottom: '14px' }}>
    {label && <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 }}>{label}</label>}
    <textarea style={{
      width: '100%', padding: '9px 12px', border: `1px solid ${cores.cinzaBorda}`,
      borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
      outline: 'none', minHeight: '80px', resize: 'vertical',
    }} {...props} />
  </div>
);

// MunicipioAutocomplete: campos casados Município + Cód. IBGE.
// - Carrega lista do IBGE conforme `uf` (com cache em memória).
// - Ao escolher um município no datalist → propaga (nome, codigoIbge) via onChange.
// - Se a API falhar → libera edição manual e mostra aviso.
// - Se já existe valor mas IBGE está vazio → mostra warning para reselecionar.
export const MunicipioAutocomplete = ({
  uf, municipio, codigoIbge, onChange,
  required = false, disabled = false, style: extraStyle,
}) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erroApi, setErroApi] = useState(null);
  const [texto, setTexto] = useState(municipio || '');
  const reqIdRef = useRef(0);

  const ufNorm = (uf || '').trim().toUpperCase();

  // Sincroniza o texto interno se o município externo mudar (ex.: edição de outro registro).
  useEffect(() => { setTexto(municipio || ''); }, [municipio]);

  // Recarrega lista quando UF muda. Cancela respostas antigas com reqIdRef.
  useEffect(() => {
    if (!ufNorm || ufNorm.length !== 2) {
      setLista([]); setErroApi(null); setLoading(false);
      return;
    }
    const meu = ++reqIdRef.current;
    setLoading(true); setErroApi(null);
    buscarMunicipios(ufNorm)
      .then(arr => {
        if (reqIdRef.current !== meu) return;
        setLista(arr); setLoading(false);
      })
      .catch(err => {
        if (reqIdRef.current !== meu) return;
        setErroApi(err.message || 'Falha ao carregar municípios');
        setLista([]); setLoading(false);
      });
  }, [ufNorm]);

  const handleTexto = (valor) => {
    setTexto(valor);
    // Tenta casar exatamente com algum município da lista (case-insensitive).
    const found = lista.find(m => m.nome.toLowerCase() === valor.toLowerCase());
    if (found) {
      onChange(found.nome, found.id);
    } else if (erroApi) {
      // Modo manual (API caiu): propaga só o texto, IBGE vai num input separado.
      onChange(valor, codigoIbge || '');
    } else {
      // Ainda digitando ou nome inválido: mantém texto local mas zera IBGE para
      // forçar nova seleção (nunca emitir com IBGE inconsistente).
      onChange(valor, '');
    }
  };

  const limpar = () => {
    setTexto('');
    onChange('', '');
  };

  const ibgeFaltando = required && !codigoIbge;
  const datalistId = `municipios-${ufNorm}`;
  const labelStyle = { fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: 500 };
  const inputBase = {
    width: '100%', padding: '9px 12px', border: `1px solid ${ibgeFaltando ? cores.vermelho : cores.cinzaBorda}`,
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ gridColumn: '1/3', marginBottom: '14px', ...extraStyle }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto', gap: '8px', alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Município{required ? ' *' : ''}</label>
          <input
            list={erroApi ? undefined : datalistId}
            value={texto}
            disabled={disabled || (!ufNorm)}
            placeholder={!ufNorm ? 'Selecione a UF antes' : (loading ? 'Carregando municípios...' : (erroApi ? 'Digite o nome do município' : 'Digite ou escolha o município'))}
            onChange={e => handleTexto(e.target.value)}
            style={inputBase}
          />
          {!erroApi && (
            <datalist id={datalistId}>
              {lista.map(m => <option key={m.id} value={m.nome} />)}
            </datalist>
          )}
        </div>
        <div>
          <label style={labelStyle}>Cód. Município IBGE{required ? ' *' : ''}</label>
          <input
            value={codigoIbge || ''}
            readOnly={!erroApi}
            disabled={disabled}
            onChange={e => {
              if (!erroApi) return;
              // Em modo fallback (API caiu) o usuário digita o IBGE manualmente.
              const digits = e.target.value.replace(/\D/g, '').slice(0, 7);
              onChange(texto, digits);
            }}
            placeholder={erroApi ? '7 dígitos' : 'Auto'}
            maxLength={7}
            style={{
              ...inputBase,
              background: erroApi ? '#fff' : '#f5f5f3',
              color: erroApi ? '#000' : '#444',
              fontFamily: 'ui-monospace, Menlo, Monaco, monospace',
            }}
          />
        </div>
        <button
          type="button"
          onClick={limpar}
          disabled={disabled || (!texto && !codigoIbge)}
          title="Limpar município"
          style={{
            height: '38px', padding: '0 12px', border: `1px solid ${cores.cinzaBorda}`,
            borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '12px',
            color: cores.cinzaTxt, opacity: (disabled || (!texto && !codigoIbge)) ? 0.5 : 1,
          }}
        >Limpar</button>
      </div>
      {erroApi && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: cores.amareloTxt, background: cores.amareloClaro, padding: '6px 10px', borderRadius: '6px' }}>
          ⚠️ Não foi possível carregar a lista de municípios do IBGE ({erroApi}). Preencha o nome e o código IBGE manualmente.
        </div>
      )}
      {!erroApi && ufNorm && texto && !codigoIbge && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: cores.amareloTxt, background: cores.amareloClaro, padding: '6px 10px', borderRadius: '6px' }}>
          ⚠️ Código IBGE ausente. Selecione novamente o município na lista para preencher automaticamente.
        </div>
      )}
      {ibgeFaltando && (
        <div style={{ marginTop: '4px', fontSize: '12px', color: cores.vermelhoTxt }}>
          Campo obrigatório.
        </div>
      )}
    </div>
  );
};

export const Btn = ({ children, variant = 'primary', size = 'md', style: extraStyle, ...props }) => {
  const variants = {
    primary: { background: cores.verde, color: '#fff', border: 'none' },
    danger: { background: cores.vermelho, color: '#fff', border: 'none' },
    outline: { background: 'transparent', color: '#333', border: `1px solid ${cores.cinzaBorda}` },
    ghost: { background: 'transparent', color: cores.azul, border: 'none', padding: '4px 8px' },
  };
  const sizes = {
    sm: { padding: '5px 12px', fontSize: '12px' },
    md: { padding: '9px 20px', fontSize: '14px' },
    lg: { padding: '12px 28px', fontSize: '15px' },
  };
  return (
    <button style={{
      ...variants[variant], ...sizes[size],
      borderRadius: '8px', fontWeight: 500, cursor: 'pointer',
      opacity: props.disabled ? 0.5 : 1, ...extraStyle,
    }} {...props}>{children}</button>
  );
};

export const Alerta = ({ tipo = 'info', children }) => {
  const map = {
    sucesso: { bg: cores.verdeClaro, color: cores.verdeTxt, borda: '#5DCAA5' },
    erro: { bg: cores.vermelhoClaro, color: cores.vermelhoTxt, borda: '#F09595' },
    info: { bg: cores.azulClaro, color: cores.azulTxt, borda: '#85B7EB' },
    aviso: { bg: cores.amareloClaro, color: cores.amareloTxt, borda: '#FAC775' },
    reforma: { bg: cores.roxoClaro, color: cores.roxoTxt, borda: '#AFA9EC' },
  };
  const s = map[tipo] || map.info;
  return (
    <div style={{
      background: s.bg, color: s.color, padding: '12px 16px',
      borderRadius: '8px', borderLeft: `4px solid ${s.borda}`,
      fontSize: '13px', marginBottom: '12px', lineHeight: '1.5',
    }}>{children}</div>
  );
};

export const Modal = ({ titulo, aberto, onFechar, children, largura = '500px' }) => {
  if (!aberto) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000,
    }} onClick={onFechar}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '1.5rem',
        width: largura, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '17px' }}>{titulo}</h3>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// estiloLinha: opcional, (row) => objeto de estilo aplicado ao <tr>. Permite
// destacar linhas (ex.: nota legado cancelada) sem duplicar a tabela inteira.
export const Tabela = ({ colunas, dados, acoes, estiloLinha }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${cores.cinzaBorda}` }}>
          {colunas.map((col, i) => (
            <th key={i} style={{
              textAlign: col.align || 'left', padding: '10px 8px',
              color: cores.cinzaLight, fontWeight: 500, fontSize: '12px',
              textTransform: 'uppercase', letterSpacing: '0.3px',
            }}>{col.label}</th>
          ))}
          {acoes && <th style={{ textAlign: 'center', padding: '10px 8px', color: cores.cinzaLight, fontWeight: 500, fontSize: '12px' }}>AÇÕES</th>}
        </tr>
      </thead>
      <tbody>
        {dados.map((row, i) => (
          <tr key={row.id || i} style={{ borderBottom: '1px solid #f5f5f3', ...(estiloLinha ? estiloLinha(row) : null) }}>
            {colunas.map((col, j) => (
              <td key={j} style={{
                padding: '10px 8px', textAlign: col.align || 'left',
                fontWeight: col.bold ? 500 : 400,
              }}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
            {acoes && <td style={{ padding: '10px 8px', textAlign: 'center' }}>{acoes(row)}</td>}
          </tr>
        ))}
        {dados.length === 0 && (
          <tr><td colSpan={colunas.length + (acoes ? 1 : 0)} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>Nenhum registro encontrado</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const fmtCnpj = (v) => {
  if (!v || v.length !== 14) return v;
  return `${v.slice(0,2)}.${v.slice(2,5)}.${v.slice(5,8)}/${v.slice(8,12)}-${v.slice(12)}`;
};

export const fmtValor = (v) => {
  return (Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
